import { AddressIcon } from '@assets';
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal } from '@components';
import { SelectInput } from '@components/Select/SelectInput';
import { DebounceAutoComplete } from '@components/Autocomplete/DebounceAutoComplete';
import { AddressBookModel } from '@store/customers/customerDetails/addressBookTab/types';
import { closeModal, openModal } from '@store/modals';
import { AlertIcon, CheckIcon, PencilIcon } from '@atgfr8/icons';
import { IAddress } from '@typeDefinitions/addressTypes';
import { ILocationSearch } from '@typeDefinitions/locationTypes';
import { sanitizeStateInput } from '@utils/formUtils';
import { countryList } from '@views/customer/customerDetail/constants';
import AddressBookGrid from '@views/customer/customerDetail/addressBookTab/AddressBookGrid';
import { checkValidZipCode, deriveCountryFromZipCode } from '@views/ltl/helper';
import { setZipCode as setZipCodeHelper } from '@views/ltl/quoting/utils/helpers';
import {
  createEffect,
  createSignal,
  createUniqueId,
  JSX,
  Show,
} from 'solid-js';

export const ADDRESS_NOT_VALIDATED_WARNING =
  'Warning: Custom city/state entered. Address could not be automatically validated.';

type ZipCodeFieldProps = {
  type: 'origin' | 'destination';
  customerId?: number | null;
  zip: string;
  city: string;
  state: string;
  country: string;
  showAddressBookButton?: boolean;
  errors: { [key: string]: unknown };
  setFields: (field: string, value: unknown) => void;
  selectedValueBoxClass: string;
  selectedValueIconClass: string;
  onClearZipCode: (type: 'origin' | 'destination') => void;
  onInputZipCode: (e: Event, type: 'origin' | 'destination') => void;
  setAccessorialsFromAddress?: (
    address: AddressBookModel,
    type: 'origin' | 'destination',
  ) => void;
  /** Enables the manual city/state override UI (feature-flagged). */
  enableManualOverride?: boolean;
  /** False when the entered city/state bypasses the validated result. */
  addressValidated?: boolean;
};

export const ZipCodeField = (props: ZipCodeFieldProps): JSX.Element => {
  const modalId = `address-book-modal-${props.type}-${createUniqueId()}`;
  const [manualMode, setManualMode] = createSignal(false);
  // The last city/state validated via the address search (or address book),
  // used to decide whether a manual entry actually differs from it.
  let lastValidated: { city: string; state: string } | null = null;

  const setAddressValidated = (validated: boolean) => {
    props.setFields(`${props.type}AddressValidated`, validated);
  };

  const showAddressWarning = () => props.addressValidated === false;

  const matchesLastValidated = (city: string, state: string): boolean =>
    lastValidated !== null &&
    city.trim().toLowerCase() === lastValidated.city.trim().toLowerCase() &&
    state.trim().toLowerCase() === lastValidated.state.trim().toLowerCase();

  const handleManualCityChange = (city: string) => {
    props.setFields(`${props.type}City`, city);
    setAddressValidated(matchesLastValidated(city, props.state));
  };

  const handleManualStateChange = (state: string) => {
    const normalizedState = sanitizeStateInput(state);
    props.setFields(`${props.type}State`, normalizedState);
    setAddressValidated(matchesLastValidated(props.city, normalizedState));
  };

  const enterManualMode = () => {
    if (Boolean(props.city) && Boolean(props.state) && lastValidated === null) {
      // Editing a prefilled value: leaving the text unchanged stays validated.
      lastValidated = { city: props.city, state: props.state };
    }
    setManualMode(true);
  };

  // Clearing or re-typing the ZIP exits manual mode and clears the baseline.
  createEffect(() => {
    if (!props.zip) {
      setManualMode(false);
      lastValidated = null;
    }
  });

  // Every keystroke in the ZIP field blanks the country, so re-seed it from the
  // postal-code shape whenever manual entry is active without one. Guarding on
  // an empty country keeps a user's explicit selection intact.
  createEffect(() => {
    if (manualMode() && !props.country) {
      props.setFields(
        `${props.type}Country`,
        deriveCountryFromZipCode(props.zip),
      );
    }
  });

  const handleAddressSelection = (rowData: AddressBookModel) => {
    if (props.setAccessorialsFromAddress) {
      props.setAccessorialsFromAddress(rowData, props.type);
    }
    lastValidated = {
      city: typeof rowData.city === 'string' ? rowData.city : '',
      state: typeof rowData.state === 'string' ? rowData.state : '',
    };
    setAddressValidated(true);
    setManualMode(false);
    closeModal(modalId);
  };

  const openAddressBookModal = () => {
    openModal(modalId);
  };

  return (
    <>
      <Box flex={1}>
        <DebounceAutoComplete
          endpoint="Address/PostalCode/"
          useV3Client={true}
          forceAutoSelect={true}
          onItemSelect={(item) => {
            setZipCodeHelper(props.type, item as IAddress, {
              setFields: props.setFields,
            })();
            const address = item as IAddress;
            lastValidated = {
              city: address.city,
              state: address.state,
            };
            setAddressValidated(true);
            setManualMode(false);
          }}
          inputProps={{
            maxLength: 7,
          }}
          value={props.zip}
          id={`autocomplete-field-${props.type}`}
          name={`${props.type}Zip`}
          label={props.type === 'origin' ? 'Origin Zip' : 'Destination Zip'}
          customButton={(loading) =>
            !loading ? (
              <IconButton
                aria-label="select address"
                dataTestId={`select-address-${props.type}-button`}
                onClick={() => openAddressBookModal()}
                size="lg"
                style={{ padding: 0 }}
                disabled={!Boolean(props.showAddressBookButton)}
              >
                <img
                  src={AddressIcon}
                  alt="Select Address"
                  style={{
                    opacity: !Boolean(props.showAddressBookButton) ? 0.5 : 1,
                    filter: !Boolean(props.showAddressBookButton)
                      ? 'grayscale(100%)'
                      : 'none',
                  }}
                />
              </IconButton>
            ) : null
          }
          defaultValue=""
          responseFieldName="city"
          renderItem={(item) => {
            return (
              <Typography variant="body1" component="p">
                {(item as ILocationSearch).city},{' '}
                {(item as ILocationSearch).state}{' '}
                {(item as ILocationSearch).country}
              </Typography>
            );
          }}
          error={props.errors[`${props.type}Zip`] as string | null}
          onClearValue={() => props.onClearZipCode(props.type)}
          onInput={(e) => props.onInputZipCode(e, props.type)}
          onResultsChange={(count) => {
            if (!props.enableManualOverride) {
              return;
            }
            props.setFields(`${props.type}ZipHasResults`, count > 0);
          }}
          required
        />
        <Show
          when={!manualMode()}
          fallback={
            <Box
              class="flex items-center gap-1 pt-1"
              dataTestId={`manual-city-state-${props.type}`}
            >
              <TextField
                label=""
                size="sm"
                placeholder="City"
                value={props.city}
                maxLength={50}
                class="h-[32px]! flex-1 [&>input]:text-black!"
                dataTestId={`manual-city-${props.type}`}
                onChange={(value: string) => handleManualCityChange(value)}
                endIcon={<PencilIcon size={14} />}
              />
              <TextField
                label=""
                size="sm"
                placeholder="ST"
                value={props.state}
                maxLength={2}
                class={`h-[32px]! ${
                  showAddressWarning() ? 'w-[46px]!' : 'w-[64px]!'
                } [&>input]:text-black! [&>input]:uppercase`}
                dataTestId={`manual-state-${props.type}`}
                onChange={(value: string) => handleManualStateChange(value)}
              />
              <Box class="w-[78px]">
                <SelectInput
                  size="sm"
                  menuItems={countryList.map((c) => ({
                    label: c.label,
                    value: String(c.value),
                  }))}
                  value={props.country || deriveCountryFromZipCode(props.zip)}
                  onChange={(value) =>
                    props.setFields(`${props.type}Country`, value)
                  }
                  dataTestId={`manual-country-${props.type}`}
                />
              </Box>
              <Show when={showAddressWarning()}>
                <Tooltip
                  class="z-50 shrink-0"
                  placement="bottom"
                  title={ADDRESS_NOT_VALIDATED_WARNING}
                  dataTestId={`manual-address-warning-${props.type}`}
                >
                  <AlertIcon size={18} class="text-[#F59D25]! cursor-pointer" />
                </Tooltip>
              </Show>
            </Box>
          }
        >
          {Boolean(props.zip) &&
            Boolean(props.city) &&
            Boolean(props.state) && (
              <Box class={props.selectedValueBoxClass}>
                <CheckIcon
                  size={14}
                  class={props.selectedValueIconClass}
                ></CheckIcon>
                {`${props.city}, ${props.state} ${props.country}`}
                <Show when={props.enableManualOverride}>
                  <IconButton
                    aria-label="edit city and state manually"
                    title="Edit city/state manually"
                    dataTestId={`edit-city-state-${props.type}-button`}
                    onClick={enterManualMode}
                    size="xs"
                    style={{ padding: 0, 'margin-left': 'auto' }}
                  >
                    <PencilIcon size={14} />
                  </IconButton>
                </Show>
              </Box>
            )}
          {Boolean(props.enableManualOverride) &&
            Boolean(props.zip) &&
            checkValidZipCode(props.zip) &&
            !props.city && (
              <Typography
                variant="body2"
                component="p"
                class="cursor-pointer underline text-[#026EA1] text-xs! pt-1!"
                dataTestId={`enter-city-state-manually-${props.type}`}
                onClick={enterManualMode}
              >
                Enter city/state manually
              </Typography>
            )}
        </Show>
      </Box>

      <BasicModal id={modalId} title="Address Book" width="80vw" footer={false}>
        <Box class="ag-theme-alpine" height={550}>
          <AddressBookGrid
            onRowClick={handleAddressSelection}
            customerId={props.customerId as number}
            type={'LTL'}
          />
        </Box>
      </BasicModal>
    </>
  );
};
